import * as React from "react";
import { Check, X, ChevronsUpDown } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * MultiSelect - A searchable multi-select dropdown component
 * @param {Object} props
 * @param {Array} props.options - Array of {value: string, label: string}
 * @param {Array} props.value - Array of selected values
 * @param {function} props.onChange - Callback when values change
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.emptyText - Text to show when no results found
 * @param {string} props.searchPlaceholder - Search input placeholder
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.className - Additional classes for the button
 * @param {number} props.maxDisplay - Maximum number of badges to display before showing count
 * @param {string|function} props.selectedSummary - Optional compact summary text/function for selected values
 */
export const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder,
  emptyText,
  searchPlaceholder,
  disabled = false,
  className,
  maxDisplay = 2,
  maxLabelDisplay = 20,
  lockedValues = [],
  selectedSummary,
}) => {
  const { t } = useTran();
  const [open, setOpen] = React.useState(false);
  const resolvedPlaceholder = placeholder ?? t("Select options...");
  const resolvedEmptyText = emptyText ?? t("No option found.");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("Search...");
  const selectableOptions = React.useMemo(
    () => options.filter((option) => option.value !== "All"),
    [options],
  );
  const singleSelectableOption =
    selectableOptions.length === 1 ? selectableOptions[0] : null;
  const resolvedOptions = singleSelectableOption ? selectableOptions : options;
  const isSingleOptionLocked = Boolean(singleSelectableOption);
  const isLocked = (val) => lockedValues.includes(val);
  const isOptionLocked = (val) => isLocked(val) || isSingleOptionLocked;

  React.useEffect(() => {
    if (!singleSelectableOption) return;

    const singleValue = singleSelectableOption.value;

    if (value.length !== 1 || value[0] !== singleValue) {
      onChange?.([singleValue]);
    }
  }, [onChange, singleSelectableOption, value]);

  const handleSelect = (selectedValue) => {
    if (isOptionLocked(selectedValue)) return;

    const newValue = value.includes(selectedValue)
      ? value.filter((v) => v !== selectedValue)
      : [...value, selectedValue];
    onChange(newValue);
  };

  const handleRemove = (valueToRemove, e) => {
    e.stopPropagation();
    if (isOptionLocked(valueToRemove)) return;

    onChange(value.filter((v) => v !== valueToRemove));
  };

  const selectedOptions = resolvedOptions.filter((option) =>
    value.includes(option.value),
  );
  const resolvedSelectedSummary =
    typeof selectedSummary === "function"
      ? selectedSummary(selectedOptions, resolvedOptions)
      : selectedSummary;
  const displayedOptions = selectedOptions.slice(0, maxDisplay);
  const remainingCount = selectedOptions.length - maxDisplay;

  const truncateLabel = (text, maxLength = maxLabelDisplay) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-auto w-full min-w-0 justify-between dark:bg-background/30",
            className,
          )}
          disabled={disabled}
        >
          <div className="flex min-w-0 flex-1 flex-wrap gap-1 text-left">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">
                {resolvedPlaceholder}
              </span>
            ) : resolvedSelectedSummary ? (
              <span>{resolvedSelectedSummary}</span>
            ) : (
              <>
                {displayedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="mr-1 gap-1"
                  >
                    {truncateLabel(option.label)}
                    {!isOptionLocked(option.value) && (
                      <button
                        type="button"
                        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleRemove(option.value, e);
                          }
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => handleRemove(option.value, e)}
                      >
                        <X className="h-3 w-3 text-gray-200 hover:text-foreground" />
                      </button>
                    )}
                  </Badge>
                ))}
                {remainingCount > 0 && (
                  <Badge variant="secondary" className="mr-1">
                    {t("+{{count}} more", { count: remainingCount })}
                  </Badge>
                )}
              </>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[min(16rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={resolvedSearchPlaceholder} />
          <CommandList className="max-h-[300px] overscroll-contain">
            <CommandEmpty>{resolvedEmptyText}</CommandEmpty>
            <CommandGroup>
              {resolvedOptions.map((option) => {
                const isSelected = value.includes(option.value);
                const optionValue = String(option.value ?? "");
                const optionLabel = String(
                  option.label ?? option.value ?? option.key ?? "",
                );
                return (
                  <CommandItem
                    key={optionValue}
                    value={optionLabel}
                    disabled={isOptionLocked(option.value)}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isOptionLocked(option.value)}
                      className="mr-2"
                    />
                    {optionLabel}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
